<template>
  <div class="dashboard-admin-hanes">
    <b-overlay :show="isLoading" variant="light" blur="2px">
      <div class="page-header">
        <div class="header-top">
          <div>
            <h2 class="page-title">Dashboard Executivo</h2>
            <p class="page-subtitle">Visão gerencial de vendas e performance</p>
          </div>
          <div class="filters-inline">
            <b-form-datepicker v-model="filters.dataInicio" placeholder="Data Início" size="sm" class="filter-date" />
            <b-form-datepicker v-model="filters.dataFim" placeholder="Data Fim" size="sm" class="filter-date" />
            <b-button variant="primary" size="sm" @click="loadData">
              <feather-icon icon="FilterIcon" size="14" /> Filtrar
            </b-button>
          </div>
        </div>
      </div>

      <div class="stats-grid">
        <div class="stat-card stat-primary">
          <div class="stat-icon"><feather-icon icon="DollarSignIcon" size="24" /></div>
          <div class="stat-content">
            <span class="stat-label">Faturamento Total</span>
            <span class="stat-value">{{ formatCurrency(data.resumoGeral.faturamentoTotal) }}</span>
          </div>
        </div>
        <div class="stat-card stat-success">
          <div class="stat-icon"><feather-icon icon="ShoppingCartIcon" size="24" /></div>
          <div class="stat-content">
            <span class="stat-label">Total Pedidos</span>
            <span class="stat-value">{{ data.resumoGeral.totalPedidos || 0 }}</span>
          </div>
        </div>
        <div class="stat-card stat-warning">
          <div class="stat-icon"><feather-icon icon="TrendingUpIcon" size="24" /></div>
          <div class="stat-content">
            <span class="stat-label">Ticket Médio</span>
            <span class="stat-value">{{ formatCurrency(data.resumoGeral.ticketMedio) }}</span>
          </div>
        </div>
        <div class="stat-card stat-info">
          <div class="stat-icon"><feather-icon icon="UsersIcon" size="24" /></div>
          <div class="stat-content">
            <span class="stat-label">Clientes com Pedido</span>
            <span class="stat-value">{{ data.resumoGeral.clientesComPedido || 0 }}</span>
          </div>
        </div>
      </div>

      <b-row class="mt-2">
        <b-col md="8">
          <b-card class="chart-card">
            <h5 class="card-chart-title">Faturamento Mensal (R$)</h5>
            <vue-apex-charts
              v-if="faturamentoMensalOptions.xaxis.categories.length"
              type="area"
              height="350"
              :options="faturamentoMensalOptions"
              :series="faturamentoMensalSeries"
            />
            <div v-else class="no-data">Sem dados disponíveis</div>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card class="chart-card">
            <h5 class="card-chart-title">Pedidos por Status</h5>
            <vue-apex-charts
              v-if="statusPedidosOptions.labels.length"
              type="donut"
              height="350"
              :options="statusPedidosOptions"
              :series="statusPedidosSeries"
            />
            <div v-else class="no-data">Sem dados disponíveis</div>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="mt-2">
        <b-col md="6">
          <b-card class="chart-card">
            <h5 class="card-chart-title">Top 10 Clientes (por valor)</h5>
            <vue-apex-charts
              v-if="topClientesOptions.xaxis.categories.length"
              type="bar"
              height="350"
              :options="topClientesOptions"
              :series="topClientesSeries"
            />
            <div v-else class="no-data">Sem dados disponíveis</div>
          </b-card>
        </b-col>
        <b-col md="6">
          <b-card class="chart-card">
            <h5 class="card-chart-title">Top 10 Representantes (por valor)</h5>
            <vue-apex-charts
              v-if="topRepresentantesOptions.xaxis.categories.length"
              type="bar"
              height="350"
              :options="topRepresentantesOptions"
              :series="topRepresentantesSeries"
            />
            <div v-else class="no-data">Sem dados disponíveis</div>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="mt-2">
        <b-col md="4">
          <b-card class="info-card">
            <h5 class="card-chart-title">Carteira de Clientes</h5>
            <div class="info-list">
              <div class="info-item">
                <span class="info-label">Total</span>
                <span class="info-value">{{ data.carteiraClientes.totalClientes || 0 }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Ativos</span>
                <span class="info-value text-success">{{ data.carteiraClientes.ativos || 0 }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Inativos</span>
                <span class="info-value text-danger">{{ data.carteiraClientes.inativos || 0 }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Novos (30 dias)</span>
                <span class="info-value text-primary">{{ data.carteiraClientes.novos30dias || 0 }}</span>
              </div>
            </div>
          </b-card>
        </b-col>
        <b-col md="8">
          <b-card class="chart-card">
            <h5 class="card-chart-title">Volume de Pedidos por Mês</h5>
            <vue-apex-charts
              v-if="pedidosMensaisOptions.xaxis.categories.length"
              type="bar"
              height="280"
              :options="pedidosMensaisOptions"
              :series="pedidosMensaisSeries"
            />
            <div v-else class="no-data">Sem dados disponíveis</div>
          </b-card>
        </b-col>
      </b-row>
    </b-overlay>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import axios from '@/libs/axios';
import { BOverlay, BRow, BCol, BCard, BButton, BFormDatepicker } from 'bootstrap-vue';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'DashboardAdminHanes',
  components: { VueApexCharts, BOverlay, BRow, BCol, BCard, BButton, BFormDatepicker },
  data() {
    return {
      isLoading: false,
      filters: { dataInicio: '', dataFim: '' },
      data: {
        faturamentoMensal: [],
        pedidosPorStatus: [],
        topClientes: [],
        topRepresentantes: [],
        carteiraClientes: {},
        resumoGeral: {},
      },
    };
  },
  computed: {
    chartTheme() {
      return this.$store?.state?.appConfig?.layout?.skin === 'dark' ? 'dark' : 'light';
    },
    faturamentoMensalSeries() {
      return [{ name: 'Faturamento', data: this.data.faturamentoMensal.map(f => Number(f.valorTotal) || 0) }];
    },
    faturamentoMensalOptions() {
      return {
        chart: { type: 'area', toolbar: { show: false } },
        xaxis: { categories: this.data.faturamentoMensal.map(f => f.mes || '') },
        colors: ['#2563eb'],
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0 } },
        dataLabels: { enabled: false },
        yaxis: { labels: { formatter: val => this.formatCurrencyShort(val) } },
        tooltip: { theme: this.chartTheme, y: { formatter: val => this.formatCurrency(val) } },
      };
    },
    statusPedidosSeries() {
      return this.data.pedidosPorStatus.map(s => Number(s.total) || 0);
    },
    statusPedidosOptions() {
      return {
        chart: { type: 'donut' },
        labels: this.data.pedidosPorStatus.map(s => s.status || 'N/A'),
        colors: ['#16a34a', '#2563eb', '#f59e0b', '#dc2626', '#0ea5e9', '#b8c2cc'],
        legend: { position: 'bottom' },
        tooltip: { theme: this.chartTheme },
      };
    },
    topClientesSeries() {
      return [{ name: 'Valor Total', data: this.data.topClientes.map(c => Number(c.valorTotal) || 0) }];
    },
    topClientesOptions() {
      return {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
        xaxis: { categories: this.data.topClientes.map(c => (c.cliente || '').substring(0, 25)), labels: { formatter: val => this.formatCurrencyShort(val) } },
        colors: ['#16a34a'],
        dataLabels: { enabled: false },
        tooltip: { theme: this.chartTheme, y: { formatter: val => this.formatCurrency(val) } },
      };
    },
    topRepresentantesSeries() {
      return [{ name: 'Valor Total', data: this.data.topRepresentantes.map(r => Number(r.valorTotal) || 0) }];
    },
    topRepresentantesOptions() {
      return {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
        xaxis: { categories: this.data.topRepresentantes.map(r => (r.representante || '').substring(0, 25)), labels: { formatter: val => this.formatCurrencyShort(val) } },
        colors: ['#f59e0b'],
        dataLabels: { enabled: false },
        tooltip: { theme: this.chartTheme, y: { formatter: val => this.formatCurrency(val) } },
      };
    },
    pedidosMensaisSeries() {
      return [
        { name: 'Qtd Pedidos', data: this.data.faturamentoMensal.map(f => Number(f.totalPedidos) || 0) },
      ];
    },
    pedidosMensaisOptions() {
      return {
        chart: { type: 'bar', toolbar: { show: false } },
        xaxis: { categories: this.data.faturamentoMensal.map(f => f.mes || '') },
        colors: ['#0ea5e9'],
        plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
        dataLabels: { enabled: false },
        tooltip: { theme: this.chartTheme },
      };
    },
  },
  async created() {
    await this.loadData();
  },
  methods: {
    formatCurrency(value) {
      return 'R$ ' + Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatCurrencyShort(value) {
      const n = Number(value || 0);
      if (n >= 1000000) return 'R$ ' + (n / 1000000).toFixed(1) + 'M';
      if (n >= 1000) return 'R$ ' + (n / 1000).toFixed(1) + 'K';
      return 'R$ ' + n.toFixed(0);
    },
    async loadData() {
      this.isLoading = true;
      try {
        const params = {};
        if (this.filters.dataInicio) params.dataInicio = this.filters.dataInicio;
        if (this.filters.dataFim) params.dataFim = this.filters.dataFim;
        const { data } = await axios.get('/dashboard/admin-hanes', { params });
        this.data = data;
      } catch (error) {
        console.error('Erro ao carregar dashboard:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar dados do dashboard',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* 
  B2B Premium Palette (Admin Executive Dashboard):
  1: #01094e (Deep Navy)
  2: #0575b5 (Ocean Blue)
  3: #00b7d6 (Vibrant Cyan)
  4: #8fdeea (Pale Sky Blue)
  5: #d6f4fa (Ice Blue)
*/
.dashboard-admin-hanes { padding: 1rem; --info-divider-color: rgba(143, 222, 234, 0.2); }

/* Header */
.page-header { margin-bottom: 2rem; }
.header-top { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 1rem; }
.page-title { font-size: 1.75rem; font-weight: 700; color: #01094e; margin-bottom: 0.25rem; letter-spacing: -0.01em; }
.page-subtitle { color: #0575b5; font-size: 1rem; font-weight: 400; opacity: 0.8; }

/* Filters Inline */
.filters-inline { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
.filter-date { max-width: 160px; border-radius: 8px; border: 1px solid rgba(143, 222, 234, 0.6); }

/* Glassmorphic Stat Cards */
.stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 1.25rem; margin-bottom: 1.5rem; }
.stat-card { 
  background: rgba(255, 255, 255, 0.85); backdrop-filter: blur(10px); border: 1px solid rgba(143, 222, 234, 0.4); 
  border-radius: 12px; padding: 1.5rem; display: flex; align-items: center; gap: 1.25rem; 
  box-shadow: 0 8px 32px rgba(1, 9, 78, 0.04); position: relative; 
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.3s ease; 
}
.stat-card:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(1, 9, 78, 0.08); border-color: rgba(0, 183, 214, 0.4); }

/* Base Stat Icon */
.stat-icon { 
  width: 56px; height: 56px; border-radius: 12px; display: flex; align-items: center; justify-content: center; 
  background: linear-gradient(135deg, rgba(5, 117, 181, 0.1), rgba(0, 183, 214, 0.1));
  color: #0575b5; border: 1px solid rgba(0, 183, 214, 0.15); transition: all 0.3s ease; 
}
.stat-card:hover .stat-icon { background: linear-gradient(135deg, #0575b5, #00b7d6); color: #fff; box-shadow: 0 4px 15px rgba(0, 183, 214, 0.4); border-color: transparent; }

/* Contextual colors inside icons */
.stat-primary .stat-icon { background: rgba(var(--primary-rgb, 37,99,235),0.1); color: var(--sf-accent, #2563eb); border-color: rgba(var(--primary-rgb, 37,99,235),0.2); }
.stat-card.stat-primary:hover .stat-icon { background: linear-gradient(135deg, var(--primary, #2563eb), var(--sf-accent, #3b82f6)); box-shadow: 0 4px 15px rgba(var(--primary-rgb, 37,99,235),0.4); color: #fff; }

.stat-success .stat-icon { background: rgba(22,163,74,0.1); color: #16a34a; border-color: rgba(22,163,74,0.2); }
.stat-card.stat-success:hover .stat-icon { background: linear-gradient(135deg, #16a34a, #4ade80); box-shadow: 0 4px 15px rgba(22,163,74,0.4); color: #fff; }

.stat-warning .stat-icon { background: rgba(245,158,11,0.1); color: #f59e0b; border-color: rgba(245,158,11,0.2); }
.stat-card.stat-warning:hover .stat-icon { background: linear-gradient(135deg, #f59e0b, #fbbf24); box-shadow: 0 4px 15px rgba(245,158,11,0.4); color: #fff; }

.stat-info .stat-icon { background: rgba(14,165,233,0.1); color: #0ea5e9; border-color: rgba(14,165,233,0.2); }
.stat-card.stat-info:hover .stat-icon { background: linear-gradient(135deg, #0ea5e9, #38bdf8); box-shadow: 0 4px 15px rgba(14,165,233,0.4); color: #fff; }

/* Typography inside Stats */
.stat-label { display: block; font-size: 0.9rem; font-weight: 500; color: #0575b5; opacity: 0.8; margin-bottom: 0.25rem; }
.stat-value { display: block; font-size: 1.75rem; font-weight: 700; color: #01094e; line-height: 1.1; }

/* Chart and Info Cards */
.chart-card, .info-card { 
  background: rgba(255, 255, 255, 0.9); backdrop-filter: blur(8px); border: 1px solid rgba(143, 222, 234, 0.25); border-radius: 12px; box-shadow: 0 8px 32px rgba(1, 9, 78, 0.04); transition: box-shadow 0.3s ease; 
}
.chart-card:hover, .info-card:hover { box-shadow: 0 12px 40px rgba(1, 9, 78, 0.08); }
.info-card { height: 100%; display: flex; flex-direction: column; }
.card-chart-title { font-size: 1.1rem; font-weight: 700; color: #01094e; margin-bottom: 1.25rem; letter-spacing: -0.01em; }

/* Info List */
.info-list { display: flex; flex-direction: column; gap: 0.75rem; flex: 1; justify-content: center; }
.info-item { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 0; border-bottom: 1px solid var(--info-divider-color); }
.info-item:last-child { border-bottom: none; }
.info-label { color: #0575b5; font-size: 0.95rem; font-weight: 500; }
.info-value { font-weight: 700; font-size: 1.15rem; color: #01094e; }
.info-value.text-success { color: #16a34a !important; }
.info-value.text-danger { color: #dc2626 !important; }
.info-value.text-primary { color: #2563eb !important; }

.no-data { text-align: center; padding: 3rem; color: #8fdeea; font-size: 1rem; font-weight: 500; }

/* ================= Dark Layout Overrides ================= */
body.dark-layout .dashboard-admin-hanes .page-title { color: #d6f4fa; }
body.dark-layout .dashboard-admin-hanes .page-subtitle { color: #8fdeea; }
body.dark-layout .dashboard-admin-hanes .stat-card,
body.dark-layout .dashboard-admin-hanes .chart-card,
body.dark-layout .dashboard-admin-hanes .info-card {
  background: rgba(1, 9, 78, 0.6); border-color: rgba(143, 222, 234, 0.1); box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}
body.dark-layout .dashboard-admin-hanes .stat-card:hover,
body.dark-layout .dashboard-admin-hanes .chart-card:hover,
body.dark-layout .dashboard-admin-hanes .info-card:hover {
  background: rgba(1, 9, 78, 0.8); border-color: rgba(0, 183, 214, 0.3); box-shadow: 0 12px 40px rgba(0, 183, 214, 0.15);
}
body.dark-layout .dashboard-admin-hanes .stat-label,
body.dark-layout .dashboard-admin-hanes .info-label { color: #8fdeea; }
body.dark-layout .dashboard-admin-hanes .stat-value,
body.dark-layout .dashboard-admin-hanes .info-value,
body.dark-layout .dashboard-admin-hanes .card-chart-title { color: #d6f4fa; }
body.dark-layout .dashboard-admin-hanes { --info-divider-color: rgba(143, 222, 234, 0.15); }
body.dark-layout .dashboard-admin-hanes .info-value.text-success { color: #4ade80 !important; }
body.dark-layout .dashboard-admin-hanes .info-value.text-danger { color: #f87171 !important; }
body.dark-layout .dashboard-admin-hanes .info-value.text-primary { color: var(--sf-accent, #60a5fa) !important; }
body.dark-layout .dashboard-admin-hanes .no-data { color: #8fdeea; }

body.dark-layout .dashboard-admin-hanes .stat-icon { background: rgba(0, 183, 214, 0.1); color: #00b7d6; border-color: rgba(0, 183, 214, 0.2); }
body.dark-layout .dashboard-admin-hanes .stat-card:hover .stat-icon { background: linear-gradient(135deg, #0575b5, #00b7d6); color: #fff; box-shadow: 0 0 15px rgba(0, 183, 214, 0.5); border-color: transparent; }

/* Specific dark mode context tints */
body.dark-layout .dashboard-admin-hanes .stat-primary .stat-icon { background: rgba(37,99,235,0.1); border-color: rgba(37,99,235,0.2); }
body.dark-layout .dashboard-admin-hanes .stat-success .stat-icon { background: rgba(22,163,74,0.1); border-color: rgba(22,163,74,0.2); }
body.dark-layout .dashboard-admin-hanes .stat-warning .stat-icon { background: rgba(245,158,11,0.1); border-color: rgba(245,158,11,0.2); }
body.dark-layout .dashboard-admin-hanes .stat-info .stat-icon { background: rgba(14,165,233,0.1); border-color: rgba(14,165,233,0.2); }
</style>
